import React, { useState, useEffect } from 'react';
import * as mui from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import appService from '../../services/ApplicationService';
import { useParams } from 'react-router-dom';

const FileUploadUserBulk = ({ onSuccess, appName, isDialog = false }) => {
  const [users, setUsers] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [fileProcessed, setFileProcessed] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { id } = useParams();

  // Required columns for user upload
  const requiredColumns = ['APP_NAME', 'USER_ID', 'EMAIL_ADDRESS', 'FIRST_NAME', 'LAST_NAME', 'ROLE_NAME', 'STATUS', 'TYPE'];

  // Add a new state variable for tracking corrected dates
  const [correctedDatesCount, setCorrectedDatesCount] = useState(0);

  useEffect(() => {
    setFileProcessed(false);
  }, []);

  // Handle drag over event
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  // Handle drag leave event
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  // Handle file drop event
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  // Handle file input change event
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  // Clear file and reset state
  const handleClearFile = () => {
    setFileName('');
    setUsers([]);
    setValidationError('');
    setFileProcessed(false);
    setProcessingResults(null);
    setCorrectedDatesCount(0);
    setPage(1);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Download template
  const handleDownloadTemplate = () => {
    // Create a CSV with README and FIELD DESCRIPTIONS at the top
    let csvContent = 'README,This template is for User Management. IMPORTANT: All dates must be in YYYY-MM-DD format.,Required fields are marked with *,For existing users records will be updated,For new users records will be created.\n';
    csvContent += 'FIELD DESCRIPTIONS,Application name,User ID,User email address,User first name,User last name,User role in application,Status (ACTIVE/INACTIVE),Type (USER_ACCOUNT/SYSTEM_ACCOUNT/OTHER),Date granted (YYYY-MM-DD),Date revoked (YYYY-MM-DD),Last login (YYYY-MM-DD)\n';
    csvContent += ',,,,,,,,,,\n';
    csvContent += 'APP_NAME,USER_ID,EMAIL_ADDRESS,FIRST_NAME,LAST_NAME,ROLE_NAME,STATUS,TYPE,DATE_GRANTED,DATE_REVOKED,LAST_LOGIN\n';
    
    // Add sample data rows
    csvContent += `"${appName || 'App 1'}","user1","user1@example.com","John","Doe","Admin","ACTIVE","USER_ACCOUNT","2023-01-15","","2023-05-20"\n`;
    csvContent += `"${appName || 'App 1'}","user2","user2@example.com","Jane","Smith","User","ACTIVE","USER_ACCOUNT","2023-02-10","","2023-05-18"\n`;
    csvContent += `"${appName || 'App 1'}","user3","user3@example.com","Alice","Johnson","Manager","INACTIVE","USER_ACCOUNT","2022-10-05","2023-04-15","2023-04-10"\n`;
    
    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.setAttribute('href', url);
    link.setAttribute('download', `user_template_${appName ? appName : 'template'}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle file upload logic
  const handleFile = (file) => {
    // Reset validation state
    setValidationError('');
    setIsValidating(true);
    setProcessingResults(null);
    setPage(1);
    
    if (!file) {
      setIsValidating(false);
      return;
    }
    
    const fileType = file.type;

    if (fileType === 'text/csv') {
      setFileName(file.name);
      parseCSV(file);
    } else {
      setValidationError('File is not supported. Make sure to upload a CSV file.');
      setIsValidating(false);
    }
  };

  // CSV Parsing function with handling for template format
  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let content = e.target.result;
        
        // Handle potential BOM character at the start of the file
        if (content.charCodeAt(0) === 0xFEFF) {
          content = content.substring(1);
        }
        
        // Normalize line endings
        content = content.replace(/\r\n|\r/g, '\n');
        
        const rows = content.split('\n');
        
        // In our template, headers are on the fourth row (index 3)
        const headerRowIndex = 3;
        
        if (rows.length < headerRowIndex + 1) {
          setValidationError('CSV file does not have enough rows. Please use the template provided.');
          setUsers([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }
        
        const headerRow = rows[headerRowIndex];
        const header = parseCSVRow(headerRow)
          .map(h => h.trim())
          .map(h => h.replace(/^["']|["']$/g, '').trim());
        
        // Validate required columns match our headers
        const missingColumns = [];
        for (const requiredCol of requiredColumns) {
          const found = header.some(h => h.toUpperCase() === requiredCol.toUpperCase());
          if (!found) {
            missingColumns.push(requiredCol);
          }
        }

        if (missingColumns.length > 0) {
          const headerDebugInfo = `Found headers in row 4: ${header.join(', ')}`;
          const expectedHeadersInfo = `Expected headers: ${requiredColumns.join(', ')}`;
          
          setValidationError(
            `Missing required columns: ${missingColumns.join(', ')}
            
            Please make sure you're using the template exactly as provided, without modifying the column headers.
            
            The headers must be on row 4 of the CSV file.
            
            ${headerDebugInfo}
            
            ${expectedHeadersInfo}
            
            Download a fresh template and try again.`
          );
          setUsers([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Parse data rows (skip to rows after the header)
        const data = rows.slice(headerRowIndex + 1)
          .filter(row => row.trim() !== '') // Filter out empty rows
          .map(row => {
            const values = parseCSVRow(row);
            const rowData = {};
            
            header.forEach((col, index) => {
              // Find which required column this matches (if any)
              const matchedRequiredCol = requiredColumns.find(
                reqCol => reqCol.toUpperCase() === col.toUpperCase()
              );
              
              if (matchedRequiredCol) {
                // If it's a required column, use the exact name from our requirements
                rowData[matchedRequiredCol] = values[index]?.trim() || '';
              } else {
                // For non-required columns, use the name as-is
                rowData[col] = values[index]?.trim() || '';
              }
            });
            
            return rowData;
          });

        // Validate that there is at least one row of data
        if (data.length === 0) {
          setValidationError('CSV file contains headers but no data rows. Make sure sample data is not removed from the template.');
          setUsers([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Also add an explicit warning if the user uploads just the template without modifications
        if (data.length === 3 && 
            data[0].USER_ID === 'user1' && 
            data[1].USER_ID === 'user2' && 
            data[2].USER_ID === 'user3') {
          setValidationError('You appear to have uploaded the unmodified template. Please replace the sample data with your actual user data.');
          setUsers([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Validate date formats
        const invalidDateRows = [];
        const correctedDateRows = [];

        // First try to correct any invalid dates
        data.forEach((row, index) => {
          let rowFixed = false;
          
          // Try to fix DATE_GRANTED
          if (row.DATE_GRANTED && !isValidDateFormat(row.DATE_GRANTED)) {
            const fixedDate = formatDateString(row.DATE_GRANTED);
            if (fixedDate) {
              data[index].DATE_GRANTED = fixedDate;
              rowFixed = true;
            }
          }
          
          // Try to fix DATE_REVOKED
          if (row.DATE_REVOKED && !isValidDateFormat(row.DATE_REVOKED)) {
            const fixedDate = formatDateString(row.DATE_REVOKED);
            if (fixedDate) {
              data[index].DATE_REVOKED = fixedDate;
              rowFixed = true;
            }
          }
          
          // Try to fix LAST_LOGIN
          if (row.LAST_LOGIN && !isValidDateFormat(row.LAST_LOGIN)) {
            const fixedDate = formatDateString(row.LAST_LOGIN);
            if (fixedDate) {
              data[index].LAST_LOGIN = fixedDate;
              rowFixed = true;
            }
          }
          
          // Track which rows were corrected
          if (rowFixed) {
            correctedDateRows.push({
              index: index, 
              email: row.EMAIL_ADDRESS || row.USER_ID
            });
          }
        });

        // Check if any dates still couldn't be fixed
        const stillInvalidDateRows = data.filter(row => {
          const hasInvalidDate = 
            (row.DATE_GRANTED && !isValidDateFormat(row.DATE_GRANTED)) || 
            (row.DATE_REVOKED && !isValidDateFormat(row.DATE_REVOKED)) || 
            (row.LAST_LOGIN && !isValidDateFormat(row.LAST_LOGIN));
          return hasInvalidDate;
        });

        if (stillInvalidDateRows.length > 0) {
          const invalidDatesDisplay = stillInvalidDateRows
            .slice(0, 3)
            .map(row => {
              if (row.DATE_GRANTED && !isValidDateFormat(row.DATE_GRANTED)) {
                return `${row.EMAIL_ADDRESS || row.USER_ID}: DATE_GRANTED "${row.DATE_GRANTED}"`;
              } else if (row.DATE_REVOKED && !isValidDateFormat(row.DATE_REVOKED)) {
                return `${row.EMAIL_ADDRESS || row.USER_ID}: DATE_REVOKED "${row.DATE_REVOKED}"`;
              } else {
                return `${row.EMAIL_ADDRESS || row.USER_ID}: LAST_LOGIN "${row.LAST_LOGIN}"`;
              }
            })
            .join(', ');
            
          setValidationError(
            `Invalid date formats found in ${stillInvalidDateRows.length} rows that couldn't be auto-corrected. All dates must be in YYYY-MM-DD format. Examples: ${invalidDatesDisplay}${stillInvalidDateRows.length > 3 ? '...' : ''}`
          );
          setUsers([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Show information about corrected dates if any were fixed
        if (correctedDateRows.length > 0) {
          setCorrectedDatesCount(correctedDateRows.length);
        }
        
        // Validate that inactive users have a DATE_REVOKED value
        const missingDateRevokedRows = data.filter(row => {
          return (row.STATUS?.toUpperCase() === 'INACTIVE') && 
                 (!row.DATE_REVOKED || row.DATE_REVOKED.trim() === '');
        });

        if (missingDateRevokedRows.length > 0) {
          const invalidRowsDisplay = missingDateRevokedRows
            .slice(0, 3)
            .map(row => `"${row.USER_ID || row.EMAIL_ADDRESS}"`)
            .join(', ');
            
          setValidationError(
            `For users with "Inactive" status, DATE_REVOKED must not be empty. Found ${missingDateRevokedRows.length} records missing DATE_REVOKED: ${invalidRowsDisplay}${missingDateRevokedRows.length > 3 ? '...' : ''}`
          );
          setUsers([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Validate email format for all rows
        const invalidEmailRows = data.filter(row => {
          const email = row.EMAIL_ADDRESS;
          return email && !isValidEmail(email);
        });

        if (invalidEmailRows.length > 0) {
          const invalidEmailsDisplay = invalidEmailRows
            .slice(0, 3)
            .map(row => `"${row.EMAIL_ADDRESS}"`)
            .join(', ');
            
          setValidationError(
            `Invalid email format found in ${invalidEmailRows.length} rows. Examples: ${invalidEmailsDisplay}${invalidEmailRows.length > 3 ? '...' : ''}`
          );
          setUsers([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Validate status values
        const validStatuses = ['ACTIVE', 'INACTIVE'];
        const invalidStatusRows = data.filter(row => {
          const status = row.STATUS?.toUpperCase();
          return status && !validStatuses.includes(status);
        });

        if (invalidStatusRows.length > 0) {
          const invalidStatusesDisplay = invalidStatusRows
            .slice(0, 3)
            .map(row => `"${row.STATUS}"`)
            .join(', ');
            
          setValidationError(
            `Invalid STATUS values found in ${invalidStatusRows.length} rows. Examples: ${invalidStatusesDisplay}${invalidStatusRows.length > 3 ? '...' : ''}. Valid values are: ACTIVE, INACTIVE`
          );
          setUsers([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Validate TYPE values
        const validTypes = ['USER_ACCOUNT', 'SYSTEM_ACCOUNT', 'OTHER'];
        const invalidTypeRows = data.filter(row => {
          const type = row.TYPE?.toUpperCase();
          return type && !validTypes.includes(type);
        });

        if (invalidTypeRows.length > 0) {
          const invalidTypesDisplay = invalidTypeRows
            .slice(0, 3)
            .map(row => `"${row.TYPE}"`)
            .join(', ');
            
          setValidationError(
            `Invalid TYPE values found in ${invalidTypeRows.length} rows. Examples: ${invalidTypesDisplay}${invalidTypeRows.length > 3 ? '...' : ''}. Valid values are: USER_ACCOUNT, SYSTEM_ACCOUNT, OTHER`
          );
          setUsers([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // If appName is provided, validate that all rows have the correct APP_NAME
        if (appName) {
          const invalidAppRows = data.filter(row => row.APP_NAME !== appName);
          if (invalidAppRows.length > 0) {
            setValidationError(
              `All rows must have APP_NAME set to "${appName}". Found ${invalidAppRows.length} rows with different values.`
            );
            setUsers([]);
            setIsValidating(false);
            setFileProcessed(false);
            return;
          }
        }

        // Set the validated data
        setUsers(data);
        setIsValidating(false);
        setFileProcessed(true);
      } catch (error) {
        setValidationError(`Error parsing CSV file: ${error.message}`);
        setIsValidating(false);
        setFileProcessed(false);
      }
    };
    
    reader.onerror = (error) => {
      setValidationError(`Error reading file: ${error}`);
      setIsValidating(false);
      setFileProcessed(false);
    };
    
    reader.readAsText(file);
  };

  // Helper function to validate email format
  const isValidEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  // Helper function to validate and format date strings with better parsing
  const formatDateString = (dateStr) => {
    if (!dateStr || dateStr.trim() === '') return '';
    
    try {
      const trimmedStr = dateStr.trim();
      
      // Check if already in YYYY-MM-DD format
      if (isValidDateFormat(trimmedStr)) {
        return trimmedStr;
      }
      
      // Handle common date formats
      let date;
      
      // Check for MM/DD/YYYY format
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmedStr)) {
        const [month, day, year] = trimmedStr.split('/');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      // Check for DD/MM/YYYY format
      else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmedStr)) {
        const [day, month, year] = trimmedStr.split('/');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      // Check for MM-DD-YYYY format
      else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(trimmedStr)) {
        const [month, day, year] = trimmedStr.split('-');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      // Check for DD-MM-YYYY format
      else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(trimmedStr)) {
        const [day, month, year] = trimmedStr.split('-');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      // Check for YYYY/MM/DD format
      else if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(trimmedStr)) {
        const [year, month, day] = trimmedStr.split('/');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      // Try default JavaScript date parsing as a fallback
      else {
        date = new Date(trimmedStr);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '';
      }
      
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  // Helper function to check if a date is in valid YYYY-MM-DD format
  const isValidDateFormat = (dateStr) => {
    if (!dateStr || dateStr.trim() === '') return true;
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateStr)) return false;
    
    // Validate the actual date
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  };

  // Helper function to parse a CSV row (handles quoted values with commas)
  const parseCSVRow = (row) => {
    const values = [];
    let inQuotes = false;
    let currentValue = '';
    
    for (let i = 0; i < row.length; i++) {
      const char = row[i];

      if (char === '"' && (i === 0 || row[i - 1] !== '\\')) {
        // Toggle inQuotes when encountering a quote
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        // If not in quotes, treat comma as separator
        values.push(currentValue);
        currentValue = '';
      } else {
        // Add char to the current value
        currentValue += char;
      }
    }
    values.push(currentValue); // Push the last value
    
    // Clean up quotes from the values
    return values.map(val => {
      val = val.trim();
      // Remove surrounding quotes if they exist
      if (val.startsWith('"') && val.endsWith('"')) {
        return val.substring(1, val.length - 1);
      }
      return val;
    });
  };

  // Function to process the upload (create/update records)
  const processUpload = async () => {
    setIsValidating(true);
    
    try {
      // First try to fix any invalid date formats
      const usersWithCorrectedDates = [...users];
      let anyDatesCorrected = false;
      
      usersWithCorrectedDates.forEach((user, index) => {
        // Try to fix DATE_GRANTED
        if (user.DATE_GRANTED && !isValidDateFormat(user.DATE_GRANTED)) {
          const fixedDate = formatDateString(user.DATE_GRANTED);
          if (fixedDate) {
            usersWithCorrectedDates[index].DATE_GRANTED = fixedDate;
            anyDatesCorrected = true;
          }
        }
        
        // Try to fix DATE_REVOKED
        if (user.DATE_REVOKED && !isValidDateFormat(user.DATE_REVOKED)) {
          const fixedDate = formatDateString(user.DATE_REVOKED);
          if (fixedDate) {
            usersWithCorrectedDates[index].DATE_REVOKED = fixedDate;
            anyDatesCorrected = true;
          }
        }
        
        // Try to fix LAST_LOGIN
        if (user.LAST_LOGIN && !isValidDateFormat(user.LAST_LOGIN)) {
          const fixedDate = formatDateString(user.LAST_LOGIN);
          if (fixedDate) {
            usersWithCorrectedDates[index].LAST_LOGIN = fixedDate;
            anyDatesCorrected = true;
          }
        }
      });
      
      // If any dates were corrected, update the users state
      if (anyDatesCorrected) {
        setUsers(usersWithCorrectedDates);
      }
      
      // Check if there are still any invalid date formats
      const invalidDateRecords = usersWithCorrectedDates.filter(user => 
        (user.DATE_GRANTED && !isValidDateFormat(user.DATE_GRANTED)) || 
        (user.DATE_REVOKED && !isValidDateFormat(user.DATE_REVOKED)) || 
        (user.LAST_LOGIN && !isValidDateFormat(user.LAST_LOGIN))
      );
      
      if (invalidDateRecords.length > 0) {
        const examples = invalidDateRecords.slice(0, 3).map(user => 
          `User ${user.EMAIL_ADDRESS}: ${
            user.DATE_GRANTED && !isValidDateFormat(user.DATE_GRANTED) 
              ? `DATE_GRANTED: ${user.DATE_GRANTED}` 
              : user.DATE_REVOKED && !isValidDateFormat(user.DATE_REVOKED)
                ? `DATE_REVOKED: ${user.DATE_REVOKED}`
                : `LAST_LOGIN: ${user.LAST_LOGIN}`
          }`
        );
        
        setValidationError(
          `Found ${invalidDateRecords.length} records with date formats that couldn't be automatically corrected. All dates must be in YYYY-MM-DD format. Examples: ${examples.join('; ')}${invalidDateRecords.length > 3 ? '...' : ''}`
        );
        setIsValidating(false);
        return;
      }
      
      // Prepare data - ensure dates are in the right format
      const formattedData = usersWithCorrectedDates.map(user => {
        const formattedUser = { ...user };
        
        // Format all date fields as YYYY-MM-DD for Django or null for empty values
        // Empty strings, whitespace-only strings, or non-parseable dates should become null
        if (formattedUser.DATE_GRANTED && formattedUser.DATE_GRANTED.trim() !== '') {
          const formattedDate = formatDateString(formattedUser.DATE_GRANTED);
          formattedUser.DATE_GRANTED = formattedDate || null;
        } else {
          formattedUser.DATE_GRANTED = null;
        }
        
        if (formattedUser.DATE_REVOKED && formattedUser.DATE_REVOKED.trim() !== '') {
          const formattedDate = formatDateString(formattedUser.DATE_REVOKED);
          formattedUser.DATE_REVOKED = formattedDate || null;
        } else {
          formattedUser.DATE_REVOKED = null;
        }
        
        if (formattedUser.LAST_LOGIN && formattedUser.LAST_LOGIN.trim() !== '') {
          const formattedDate = formatDateString(formattedUser.LAST_LOGIN);
          formattedUser.LAST_LOGIN = formattedDate || null;
        } else {
          formattedUser.LAST_LOGIN = null;
        }
        
        return formattedUser;
      });
      
      const response = await appService.uploadBulkUsers(id, formattedData);
      setProcessingResults(response);
      
      if (response && response.success) {
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(response);
        }
      }
    } catch (error) {
      setValidationError(`Error processing upload: ${error.message || 'Unknown error'}`);
    } finally {
      setIsValidating(false);
    }
  };

  // Calculate pagination values
  const pageCount = Math.ceil(users.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, users.length);
  const paginatedData = users.slice(startIndex, endIndex);

  return (
    <mui.Box sx={{ padding: isDialog ? '0' : '16px 24px' }}>
      {!isDialog && (
        <mui.Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <mui.Typography variant="h6" sx={{ color: '#333', fontSize: '1.25rem', fontWeight: 500 }}>
            Bulk User Management
          </mui.Typography>
          <mui.Box>
            <mui.Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              size="small"
              sx={{ 
                textTransform: 'none',
                borderColor: 'rgba(0,0,0,0.12)',
                color: '#046FB2',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: '#046FB2',
                  backgroundColor: 'rgba(4, 111, 178, 0.04)'
                }
              }}
            >
              Download Template
            </mui.Button>
          </mui.Box>
        </mui.Box>
      )}

      {isDialog && (
        <mui.Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <mui.Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            size="small"
            sx={{ 
              textTransform: 'none',
              borderColor: 'rgba(0,0,0,0.12)',
              color: '#046FB2',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#046FB2',
                backgroundColor: 'rgba(4, 111, 178, 0.04)'
              }
            }}
          >
            Download Template
          </mui.Button>
        </mui.Box>
      )}

      <mui.Typography variant="body2" sx={{ mb: 1, color: '#555' }}>
        Upload a CSV file to add or update user records. New user records will be created, and existing users (matched by email address) will be updated with the provided information.
      </mui.Typography>
      
      <mui.Box sx={{ 
        mb: 2, 
        p: 1.5, 
        bgcolor: 'rgba(4, 111, 178, 0.08)', 
        borderRadius: '6px',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <InfoOutlinedIcon sx={{ fontSize: '1rem', mr: 1, color: '#046FB2' }} />
          <mui.Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
            Important Notes
          </mui.Typography>
        </mui.Box>
        <mui.Typography variant="body2" sx={{ ml: 3, mb: 0.5 }}>
          • For <strong>existing users</strong> (matched by email address), all fields except USER_ID, FIRST_NAME, LAST_NAME will be updated.
        </mui.Typography>
        <mui.Typography variant="body2" sx={{ ml: 3, mb: 0.5 }}>
          • For <strong>new users</strong>, all required fields must be provided.
        </mui.Typography>
        <mui.Typography variant="body2" sx={{ ml: 3, mb: 0.5 }}>
          • For users with <strong>STATUS="INACTIVE"</strong>, the <strong>DATE_REVOKED</strong> field must not be empty.
        </mui.Typography>
        <mui.Typography variant="body2" sx={{ ml: 3 }}>
          • The <strong>LAST_UPDATED</strong> timestamp will be automatically set during the upload process.
        </mui.Typography>
      </mui.Box>

      {!users.length > 0 && (
        <mui.Paper
          elevation={0}
          sx={{
            border: dragging 
              ? '2px solid #046FB2' 
              : '2px dashed rgba(0, 0, 0, 0.12)',
            borderRadius: '8px',
            padding: '32px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragging 
              ? 'rgba(4, 111, 178, 0.04)' 
              : '#fafafa',
            marginBottom: '20px',
            transition: 'all 0.2s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('user-file-input').click()}
        >
          {isValidating && (
            <mui.Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
              <mui.LinearProgress sx={{ height: 4 }} />
            </mui.Box>
          )}
          
          <input
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            id="user-file-input"
          />
          
          <mui.Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: '#046FB2', mb: 1.5, opacity: 0.7 }} />
            <mui.Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5, color: '#333' }}>
              Drag & drop your file here
            </mui.Typography>
            <mui.Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              or
            </mui.Typography>
            <mui.Button
              variant="contained"
              size="small"
              startIcon={<DescriptionIcon />}
              onClick={() => document.getElementById('user-file-input').click()}
              sx={{ 
                textTransform: 'none', 
                backgroundColor: '#046FB2',
                boxShadow: 'none',
                borderRadius: '6px',
                px: 2,
                '&:hover': {
                  backgroundColor: '#035d94',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
              }}
            >
              Browse Files
            </mui.Button>
            <mui.Typography variant="caption" sx={{ mt: 2, color: '#777' }}>
              Supported format: CSV file
            </mui.Typography>
          </mui.Box>
        </mui.Paper>
      )}

      {/* File name display */}
      {fileName && !validationError && (
        <mui.Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          backgroundColor: 'rgba(4, 111, 178, 0.08)', 
          p: 1.5, 
          pl: 2,
          borderRadius: '6px', 
          mb: 2
        }}>
          <DescriptionIcon sx={{ color: '#046FB2', mr: 1.5, fontSize: '1.25rem' }} />
          <mui.Box sx={{ flex: 1 }}>
            <mui.Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
              {fileName}
            </mui.Typography>
            <mui.Typography variant="caption" sx={{ color: '#666' }}>
              {users.length} rows • CSV file 
            </mui.Typography>
          </mui.Box>
          <mui.IconButton size="small" onClick={handleClearFile}>
            <HighlightOffIcon sx={{ fontSize: '1.25rem', color: '#777' }} />
          </mui.IconButton>
        </mui.Box>
      )}

      {/* Auto-corrected dates notification */}
      {correctedDatesCount > 0 && (
        <mui.Alert 
          severity="info" 
          sx={{ 
            mb: 2, 
            '& .MuiAlert-message': { fontWeight: 400 } 
          }}
        >
          <mui.Typography variant="body2">
            <strong>{correctedDatesCount} date values</strong> were automatically converted to the required YYYY-MM-DD format.
          </mui.Typography>
        </mui.Alert>
      )}

      {/* Validation Error message */}
      {validationError && (
        <mui.Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            '& .MuiAlert-message': { fontWeight: 400 } 
          }}
        >
          {validationError}
        </mui.Alert>
      )}

      {/* Processing Results */}
      {processingResults && (
        <mui.Box sx={{ mb: 3 }}>
          <mui.Alert 
            severity={
              processingResults.success 
                ? (processingResults.created + processingResults.updated > 0 
                    ? "success" 
                    : processingResults.skipped > 0 
                      ? "info" 
                      : "warning") 
                : "error"
            } 
            sx={{ 
              mb: 2, 
              '& .MuiAlert-message': { fontWeight: 400 } 
            }}
          >
            <mui.Typography variant="body2" gutterBottom>
              <strong>Processing complete:</strong>
            </mui.Typography>
            <mui.Typography variant="body2" component="div">
              • Created: {processingResults.created || 0} new user records
            </mui.Typography>
            <mui.Typography variant="body2" component="div">
              • Updated: {processingResults.updated || 0} existing user records
            </mui.Typography>
            <mui.Typography variant="body2" component="div">
              • Skipped: {processingResults.skipped || 0} records (no changes needed)
            </mui.Typography>
            {processingResults.errors && processingResults.errors.length > 0 && (
              <mui.Typography variant="body2" component="div">
                • Errors: {processingResults.errors.length}
              </mui.Typography>
            )}
            
            {/* Show a message when all records are skipped */}
            {processingResults.created === 0 && 
             processingResults.updated === 0 && 
             processingResults.skipped > 0 && (
              <mui.Typography variant="body2" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
                All records are already up to date. No changes were needed.
              </mui.Typography>
            )}
          </mui.Alert>
          
          {/* Show debug info if no users were created or updated */}
          {processingResults.created + processingResults.updated === 0 && (
            <mui.Paper sx={{ p: 2, mb: 2, backgroundColor: '#fff8e1' }}>
              <mui.Typography variant="subtitle2" gutterBottom>
                Debug Information
              </mui.Typography>
              <mui.Typography variant="body2" gutterBottom>
                Total records received by server: {processingResults.received_data_count || 'Unknown'}
              </mui.Typography>
              
              {processingResults.skipped_records && processingResults.skipped_records.length > 0 && (
                <>
                  <mui.Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                    <strong>Skipped Records:</strong>
                  </mui.Typography>
                  <mui.Box sx={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #e0e0e0', p: 1, mb: 2 }}>
                    {processingResults.skipped_records.map((record, index) => (
                      <mui.Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {record.email || 'Unknown email'}: {record.reason || 'Unknown reason'}
                      </mui.Typography>
                    ))}
                  </mui.Box>
                </>
              )}
              
              {processingResults.errors && processingResults.errors.length > 0 && (
                <>
                  <mui.Typography variant="body2" gutterBottom>
                    <strong>Error Details:</strong>
                  </mui.Typography>
                  <mui.Box sx={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #e0e0e0', p: 1 }}>
                    {processingResults.errors.map((error, index) => (
                      <mui.Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {error}
                      </mui.Typography>
                    ))}
                  </mui.Box>
                </>
              )}
            </mui.Paper>
          )}
        </mui.Box>
      )}

      {/* Data preview */}
      {users.length > 0 && (
        <mui.Box sx={{ mt: 3 }}>
          <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <mui.Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 500 }}>
              Data Preview
            </mui.Typography>
            <mui.Typography variant="caption" sx={{ color: '#666' }}>
              Showing {startIndex + 1}-{endIndex} of {users.length} rows
            </mui.Typography>
          </mui.Box>
          
          <mui.Box sx={{ 
            border: '1px solid rgba(224, 224, 224, 1)', 
            borderRadius: '6px', 
            overflow: 'hidden',
            mb: 2
          }}>
            <mui.Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'rgba(4, 111, 178, 0.04)' }}>
                    {Object.keys(users[0]).map((header, index) => (
                      <th
                        key={index}
                        style={{
                          padding: '10px 16px',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          color: '#333',
                          fontWeight: 600,
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          whiteSpace: 'nowrap',
                          backgroundColor: requiredColumns.includes(header) ? 'rgba(4, 111, 178, 0.08)' : 'rgba(4, 111, 178, 0.04)'
                        }}
                      >
                        {header}
                        {requiredColumns.includes(header) && (
                          <span style={{ color: '#046FB2', marginLeft: '2px' }}>*</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row, rowIndex) => (
                    <tr key={rowIndex} style={{ backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f9fafb' }}>
                      {Object.entries(row).map(([key, value], index) => {
                        // Check if this is a date field with invalid format
                        const isDateField = key.includes('DATE');
                        const hasDateValue = value && value.trim() !== '';
                        const isInvalidDate = isDateField && hasDateValue && !isValidDateFormat(value);

                        return (
                          <td
                            key={index}
                            style={{
                              fontSize: '0.75rem',
                              padding: '8px 16px',
                              borderBottom: rowIndex < paginatedData.length - 1 ? '1px solid rgba(224, 224, 224, 1)' : 'none',
                              color: isInvalidDate ? 'red' : '#333',
                              backgroundColor: isInvalidDate ? '#ffeeee' : 'inherit',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {value || '-'}
                            {isInvalidDate && (
                              <span title="Date must be in YYYY-MM-DD format"> ⚠️</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </mui.Box>
          </mui.Box>
          
          {/* Pagination controls */}
          <mui.Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
              <mui.Typography variant="body2" sx={{ mr: 1, color: '#666' }}>
                Rows per page:
              </mui.Typography>
              <mui.FormControl variant="standard" size="small">
                <mui.Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={{ 
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': { 
                      py: 0.5, 
                      minWidth: '60px' 
                    }
                  }}
                >
                  <mui.MenuItem value={5}>5</mui.MenuItem>
                  <mui.MenuItem value={10}>10</mui.MenuItem>
                  <mui.MenuItem value={25}>25</mui.MenuItem>
                  <mui.MenuItem value={50}>50</mui.MenuItem>
                </mui.Select>
              </mui.FormControl>
            </mui.Box>
            
            <mui.Pagination
              count={pageCount}
              page={page}
              onChange={handleChangePage}
              size="small"
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '0.875rem',
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '4px',
                },
                '& .Mui-selected': {
                  backgroundColor: 'rgba(4, 111, 178, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(4, 111, 178, 0.2)',
                  }
                }
              }}
            />
          </mui.Box>
          
          <mui.Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <mui.Button
              variant="outlined"
              size="small"
              onClick={handleClearFile}
              sx={{ 
                textTransform: 'none',
                borderColor: 'rgba(0,0,0,0.12)',
                color: 'text.secondary',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: 'text.primary',
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Cancel
            </mui.Button>
            <mui.Button
              variant="contained"
              size="small"
              onClick={processUpload}
              disabled={isValidating || !fileProcessed}
              startIcon={isValidating ? <mui.CircularProgress size={16} color="inherit" /> : <CloudDoneIcon />}
              sx={{ 
                textTransform: 'none',
                borderRadius: '6px',
              }}
            >
              {isValidating ? 'Processing...' : 'Upload Users'}
            </mui.Button>
          </mui.Box>
        </mui.Box>
      )}
    </mui.Box>
  );
};

export default FileUploadUserBulk; 